import { useState, useEffect } from "react";
import { getTodayString } from "@/utils/dateUtils";
import { 
  isViewingResults, 
  setViewingResults, 
  getLastGameResult, 
  isGameCompleted, 
  markGameCompleted,
  getGameResultDate,
  saveGameResult
} from "@/lib/storage/local-storage";
import type { GameResult } from "@/hooks/useGameFlow";
import type { DailyPuzzle } from "@/lib/types/game";

interface UseGameResultViewProps {
  todaysPuzzle: DailyPuzzle | null;
  viewingPreviousResults: boolean;
  setViewingPreviousResults: (viewing: boolean) => void;
  hasCompletedToday: boolean;
  gameResult?: GameResult;
}

export function useGameResultView({
  todaysPuzzle,
  viewingPreviousResults,
  setViewingPreviousResults,
  hasCompletedToday,
  gameResult
}: UseGameResultViewProps) {
  // Track if we should display gameplay
  const [showGamePlay, setShowGamePlay] = useState(false);
  // Store latest game result to pass to result screen
  const [currentGameResult, setCurrentGameResult] = useState<GameResult | undefined>(gameResult);
  // Track internal completion state
  const [internalCompletedToday, setInternalCompletedToday] = useState<boolean>(hasCompletedToday);
  // Get today's date independently for verification
  const todayActual = getTodayString();

  // Enhanced localStorage check on initial mount
  useEffect(() => {
    if (todaysPuzzle?.date) {
      const viewingResultsFlag = isViewingResults();
      const completedPuzzle = isGameCompleted(todayActual);
      const savedResult = getLastGameResult();
      const resultDate = getGameResultDate();
      
      // CRITICAL FIX: Only consider saved results that match TODAY'S date
      // Use the actual today's date, not the puzzle date for comparison
      const isForToday = resultDate === todayActual;
      
      console.log("GamePlayResultView initial check:", {
        viewingResultsFlag,
        completedPuzzle,
        hasCompletedToday,
        hasSavedResult: !!savedResult,
        isSavedResultForToday: isForToday,
        todaysPuzzleDate: todaysPuzzle.date,
        actualTodayDate: todayActual,
        savedResultDate: resultDate
      });
      
      // Update our internal completion state
      // CRITICAL FIX: Only consider completed if it's explicitly for today's ACTUAL date
      setInternalCompletedToday(hasCompletedToday || completedPuzzle || (!!savedResult && isForToday));
      
      // If we have a saved result or the game is marked as completed, show results
      if ((viewingResultsFlag || completedPuzzle || (savedResult && isForToday)) && !viewingPreviousResults) {
        // Only auto-show results if the result is for TODAY'S date
        if (isForToday) {
          console.log("Auto-showing previous results based on localStorage flags");
          setViewingPreviousResults(true);
          
          if (savedResult) {
            console.log("Using saved game result:", savedResult.result);
            setCurrentGameResult(savedResult);
            
            // If we have a saved result, ensure the game is marked as completed
            if (savedResult.completedDate && savedResult.completedDate === todayActual) {
              markGameCompleted(savedResult.completedDate);
              
              // Ensure we save the result again to maintain it in storage
              // This helps refresh any potential expiring storage
              saveGameResult(savedResult);
            }
          }
        } else {
          // If we have results from a previous day, don't auto-show them
          // but keep the viewing flag in case user explicitly goes to results
          console.log("Saved results are for a different date - not auto-showing");
          setViewingPreviousResults(false);
        }
      }
    }
  }, [todaysPuzzle, hasCompletedToday, viewingPreviousResults, setViewingPreviousResults, gameResult, todayActual]);

  // Debug logs to track loading state and component rendering
  useEffect(() => {
    console.log("GamePlayResultView rendered with states:", {
      viewingPreviousResults,
      hasCompletedToday: internalCompletedToday,
      hasPuzzle: !!todaysPuzzle,
      showGamePlay,
      hasGameResult: !!gameResult || !!currentGameResult,
      todayDate: todayActual,
      puzzleDate: todaysPuzzle?.date
    });
  }, [viewingPreviousResults, internalCompletedToday, todaysPuzzle, showGamePlay, gameResult, currentGameResult, todayActual]);

  return {
    showGamePlay,
    setShowGamePlay,
    currentGameResult,
    setCurrentGameResult,
    internalCompletedToday,
    todayActual
  };
}
